/**
 * LarineCo landing — vanilla JS for hero carousel + solution tabs.
 * Keep Tailwind classes as in Lovable/React; this file only replaces React/Radix/Embla behaviour.
 *
 * Carousel — root element: [data-larine-carousel]
 *   Inside:
 *     [data-carousel-viewport]  — scroll container (snap-x mandatory)
 *     [data-carousel-slide]     — one per slide (basis-full shrink-0 grow-0 snap-start)
 *     Important: ancestors in CSS grid/flex usually need min-w-0/max-w-full so the scroll area can narrow and overflow-x works.
 *     [data-carousel-prev] / [data-carousel-next] — optional arrows
 *     [data-carousel-dots] — optional container; dots are injected here
 *
 * Tabs — root: [data-larine-tabs]
 *   [data-tab-trigger="VALUE"] toggles visibility of matching [data-tab-panel="VALUE"].
 *   Optional: set id on the root for stable generated trigger/panel IDs (recommended if multiple tab widgets on one page share the same value keys).
 */

(function () {
  "use strict";

  var tabsInstanceSeq = 0;

  /**
   * @param {HTMLElement} root
   * @param {string} sel
   */
  function qs(root, sel) {
    return root.querySelector(sel);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} sel
   * @returns {HTMLElement[]}
   */
  function qsa(root, sel) {
    return Array.from(root.querySelectorAll(sel));
  }

  /** @param {HTMLElement} carouselRoot */
  function initCarousel(carouselRoot) {
    if (carouselRoot.dataset.larineCarouselReady === "1") return;

    var viewport = qs(carouselRoot, "[data-carousel-viewport]");
    if (!viewport) return;

    /** Belt-and-braces when host layout (e.g. grid) omits Tailwind min-w-0 utilities */
    carouselRoot.style.minWidth = "0";
    viewport.style.minWidth = "0";
    viewport.style.overflowX = "auto";
    if (viewport.parentElement instanceof HTMLElement) {
      viewport.parentElement.style.minWidth = "0";
    }

    var slides = qsa(carouselRoot, "[data-carousel-slide]").filter(function (el) {
      return viewport.contains(el);
    });
    var count = slides.length;
    if (!count) return;

    var prevBtn = qs(carouselRoot, "[data-carousel-prev]");
    var nextBtn = qs(carouselRoot, "[data-carousel-next]");
    var dotsHost = qs(carouselRoot, "[data-carousel-dots]");
    /** @type {HTMLButtonElement[]} */
    var dotButtons = [];

    /** Use slide width — matches scroll snap row better than rounding errors on clientWidth. */
    function getSlideStride() {
      if (!slides.length) return viewport.clientWidth || 1;
      var ow = slides[0].offsetWidth;
      return ow > 0 ? ow : viewport.clientWidth || 1;
    }

    function wrapIndex(i) {
      var n = i % count;
      if (n < 0) n += count;
      return n;
    }

    function scrollIndex() {
      var w = getSlideStride();
      if (!w) return 0;
      return wrapIndex(Math.round(viewport.scrollLeft / w));
    }

    /** @param {number} index @param {boolean} smooth */
    function goTo(index, smooth) {
      var w = getSlideStride();
      var target = wrapIndex(index);
      viewport.scrollTo({
        left: target * w,
        behavior: smooth ? "smooth" : "auto",
      });
    }

    /** Re-align scroll position after layout / resize (fixes grid min-width quirks). */
    function reflowCarousel() {
      var w = getSlideStride();
      if (!w || !slides[0]) return;
      var i = wrapIndex(Math.round(viewport.scrollLeft / w));
      viewport.scrollLeft = i * w;
      syncDots(i);
    }

    function syncDots(activeIdx) {
      var current = wrapIndex(activeIdx);
      dotButtons.forEach(function (btn, i) {
        var on = i === current;
        btn.setAttribute("aria-current", on ? "true" : "false");

        btn.classList.remove("w-8", "w-2", "bg-brand-blue", "bg-white/30", "hover:bg-white/50");
        if (on) {
          btn.classList.add("w-8", "bg-brand-blue");
        } else {
          btn.classList.add("w-2", "bg-white/30", "hover:bg-white/50");
        }
      });
    }

    function buildDots() {
      if (!dotsHost) return;
      dotsHost.textContent = "";
      dotButtons = [];

      var startIdx = scrollIndex();

      for (var i = 0; i < count; i++) {
        (function (idx) {
          var btn = document.createElement("button");
          btn.type = "button";
          btn.className =
            "h-2 rounded-full transition-all " +
            (idx === startIdx ? "w-8 bg-brand-blue" : "w-2 bg-white/30 hover:bg-white/50");
          btn.setAttribute("aria-label", "Go to slide " + (idx + 1));
          btn.addEventListener("click", function () {
            goTo(idx, true);
          });
          dotsHost.appendChild(btn);
          dotButtons.push(btn);
        })(i);
      }
      syncDots(startIdx);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        syncDots(scrollIndex());
      });
    }

    viewport.addEventListener("scroll", onScroll, { passive: true });

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        var idx = scrollIndex();
        if (idx === 0) {
          goTo(count - 1, false);
        } else {
          goTo(idx - 1, true);
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        var idx = scrollIndex();
        if (idx === count - 1) {
          goTo(0, false);
        } else {
          goTo(idx + 1, true);
        }
      });
    }

    var roTimer = null;
    if (typeof ResizeObserver !== "undefined") {
      var ro = new ResizeObserver(function () {
        if (roTimer) clearTimeout(roTimer);
        roTimer = setTimeout(function () {
          roTimer = null;
          reflowCarousel();
        }, 50);
      });
      ro.observe(viewport);
    } else {
      window.addEventListener("resize", function () {
        if (roTimer) clearTimeout(roTimer);
        roTimer = setTimeout(function () {
          roTimer = null;
          reflowCarousel();
        }, 120);
      });
    }

    buildDots();

    /** Two rAF frames: carousel often gets width after grid/aspect-ratio settles */
    requestAnimationFrame(function () {
      requestAnimationFrame(reflowCarousel);
    });

    carouselRoot.dataset.larineCarouselReady = "1";
  }

  /** @param {HTMLElement} tabsRoot */
  function initTabs(tabsRoot) {
    if (tabsRoot.dataset.larineTabsReady === "1") return;

    var triggers = qsa(tabsRoot, "[data-tab-trigger]");
    var panels = qsa(tabsRoot, "[data-tab-panel]");
    if (!triggers.length || !panels.length) return;

    var uid =
      tabsRoot.id ||
      tabsRoot.dataset.larineTabsUid ||
      (function () {
        var id = "larine-tabs-" + ++tabsInstanceSeq;
        tabsRoot.dataset.larineTabsUid = id;
        return id;
      })();

    function panelDomId(panelValue) {
      return uid + "-panel-" + panelValue;
    }

    function triggerDomId(triggerValue) {
      return uid + "-trigger-" + triggerValue;
    }

    function focusPanel(value) {
      panels.forEach(function (panel) {
        var matches = panel.getAttribute("data-tab-panel") === value;
        if (matches) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
        }
      });

      triggers.forEach(function (tr) {

        var v = tr.getAttribute("data-tab-trigger");
        var selected = v === value;

        tr.setAttribute("aria-selected", selected ? "true" : "false");
        tr.tabIndex = selected ? 0 : -1;

        if (selected) {
          tr.classList.add("active");
        } else {
          tr.classList.remove("active");
        }

      });
    }

    var defaultTab =
      tabsRoot.getAttribute("data-default-tab") ||
      triggers[0].getAttribute("data-tab-trigger") ||
      "";

    triggers.forEach(function (tr, i) {
      tr.setAttribute("role", "tab");
      var idPanel = tr.getAttribute("data-tab-trigger");
      if (!idPanel) return;

      var pId = panelDomId(idPanel);
      var tId = triggerDomId(idPanel);
      tr.id = tr.id || tId;
      tr.setAttribute("aria-controls", pId);

      panels.forEach(function (panel) {
        if (panel.getAttribute("data-tab-panel") === idPanel) {
          panel.id = panel.id || pId;
          panel.setAttribute("role", "tabpanel");
          panel.setAttribute("aria-labelledby", tr.id);
        }
      });

      tr.addEventListener("click", function () {
        focusPanel(idPanel);
      });

      tr.addEventListener("keydown", function (e) {
        var keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
        var k = e.key;
        if (keys.indexOf(k) === -1) return;
        e.preventDefault();
        /** @type {number} */
        var next = i;
        if (k === "ArrowRight") next = (i + 1) % triggers.length;
        else if (k === "ArrowLeft") next = (i - 1 + triggers.length) % triggers.length;
        else if (k === "Home") next = 0;
        else if (k === "End") next = triggers.length - 1;
        var nextVal = triggers[next].getAttribute("data-tab-trigger");
        if (!nextVal) return;
        focusPanel(nextVal);
        triggers[next].focus();
      });
    });

    focusPanel(defaultTab);
    tabsRoot.dataset.larineTabsReady = "1";
  }

  function initAll() {
    document.querySelectorAll("[data-larine-carousel]").forEach(function (root) {
      if (root.closest("[data-larine-carousel-ignore]")) return;
      initCarousel(/** @type {HTMLElement} */ (root));
    });
    document.querySelectorAll("[data-larine-tabs]").forEach(function (root) {
      if (root.closest("[data-larine-tabs-ignore]")) return;
      initTabs(/** @type {HTMLElement} */ (root));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  window.LarineLandingWidgets = {
    initAll: initAll,
    initCarousel: initCarousel,
    initTabs: initTabs,
  };
})();
